// XTabs: the bottom tab bar (Chat · Today · Us · Me). Sits at the bottom of its screen; screens with tabs leave
// var(--xm-tabbar-h) of room under their scroll area (or use <Screen tab="...">, which does both).
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { go } from '../router';
import type { ScreenId } from '../router';
import { Icon, type IconName } from './icons';
import s from './XTabs.module.css';

export type TabId = 'chat' | 'today' | 'us' | 'me';

const TABS: { id: TabId; label: string; to: ScreenId; icon: IconName }[] = [
  { id: 'chat', label: 'Chat', to: 'C', icon: 'chat' },
  { id: 'today', label: 'Today', to: 'T', icon: 'calendar' },
  { id: 'us', label: 'Us', to: 'U1', icon: 'home' },
  { id: 'me', label: 'Me', to: 'M1', icon: 'person' },
];

export interface XTabsProps {
  tab: TabId;
  /** Accent comes from --acc; ch is only needed outside a themed <Screen>. */
  ch?: CharId;
  /** Dot on Today (cards left to do). Shown only when Today is not the current tab. */
  badge?: boolean | number;
  /** Override navigation (default: router go()). */
  onSelect?: (to: ScreenId) => void;
}

export function XTabs({ tab, ch, badge, onSelect }: XTabsProps) {
  const dot = badge === true || (typeof badge === 'number' && badge > 0);
  return (
    <nav class={s.bar} aria-label="Main" style={ch ? ({ '--acc': C[ch].acc } as Record<string, string>) : undefined}>
      <div class={s.pill}>
        {TABS.map((t) => {
          const on = t.id === tab;
          return (
            <button
              key={t.id} type="button" class={s.tab} data-on={on ? 'true' : 'false'} aria-label={t.label}
              aria-current={on ? 'page' : undefined}
              onClick={() => { if (!on) (onSelect || go)(t.to); }}
            >
              {t.id === 'today'
                ? <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" class={s.icon}><path d="M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z" /><path d="M4 10h16M9 3v4M15 3v4" /></svg>
                : t.id === 'me'
                  ? <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" class={s.icon}><path d="M12 4a4 4 0 1 1 0 8a4 4 0 1 1 0-8z" /><path d="M4 21c1-4 4-6 8-6s7 2 8 6" /></svg>
                  : <Icon name={t.icon} size={24} class={s.icon} />}
              <span class={s.label}>{t.label}</span>
              {t.id === 'today' && dot && !on && <span class={s.dot} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
